import React, { useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'antd';
import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { createApproval, getTaskCloseReasonList } from '@/common/api/expandStore/approveworkbench';
import { isArray, isNotEmptyAny } from '@lhb/func';
import styles from './index.module.less';
import { dealApprovalSumitExtras } from '@/common/components/business/ExpandStore/ApprovalSelectNodes/config';
import ApprovalSelectNodes from '@/common/components/business/ExpandStore/ApprovalSelectNodes';
import { ApprovalTypeValue } from '@/common/components/business/ExpandStore/ts-config';
import { useMethods } from '@lhb/hook';

/**
 * 开发异动-弹框
 * @Description
 * 拓店任务：加盟（益禾堂、非益禾堂）
 * 2024/12/05 目前看来三套任务模板处理无差异，统一为同一个文件降低迭代成本，后续看情况处理
 */
const TaskChangeModal: React.FC<{
  visible: boolean;
  setVisible: (state: boolean) => void;
  detail: any;
  onRefresh: any;
}> = ({
  visible,
  setVisible,
  detail,
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const [typeOptions, setTypeOptions] = useState<any[]>([]);
  const [closeReasonOptions, setCloseReasonOptions] = useState<any[]>([]);
  const [isLock, setIsLock] = useState<boolean>(false);

  const currentType = Form.useWatch('type', form);

  useEffect(() => {
    if (visible) {
      setTypeOptions((detail && isArray(detail.buttons)) ? detail.buttons.map(itm => ({ ...itm, label: itm.name, value: itm.value })) : []);
    } else {
      form.resetFields();
      setCloseReasonOptions([]);
    }
  }, [visible]);

  const currentTargetEvent = useMemo(() => {
    if (!currentType) return {};
    const targetEvent = typeOptions?.find((itm: any) => itm.value === currentType);
    return targetEvent || {};
  }, [currentType]);

  const isTerminateTask = currentTargetEvent?.approvalTypeValue === ApprovalTypeValue.TerminateTask;

  const methods = useMethods({
    getCloseReasonOptions(res: any) {
      const list = isArray(res) ? res : (isArray(res?.list) ? res.list : []);
      return list.map((item: any) => ({
        ...item,
        label: item.name || item.label || item.closeReasonName,
        value: isNotEmptyAny(item.id) ? item.id : item.value,
      })).filter((item: any) => isNotEmptyAny(item.label) && isNotEmptyAny(item.value));
    },

    getCloseReasonList() {
      getTaskCloseReasonList().then((res) => {
        setCloseReasonOptions(methods.getCloseReasonOptions(res));
      });
    },

    onCancel() {
      setVisible(false);
    },

    onSuccess() {
      V2Message.success('开发异动申请成功');
      setVisible(false);
      onRefresh && onRefresh();
    },

    onSubmit() {
      form.validateFields().then((values: any) => {
        // 匹配选中的异动事件
        const targetEvent = typeOptions?.find((itm: any) => itm.value === values.type);
        if (!targetEvent) return;

        if (isLock) return;
        setIsLock(true);

        const extras: any = dealApprovalSumitExtras(values);
        const isTerminate = targetEvent.approvalTypeValue === ApprovalTypeValue.TerminateTask;

        const params = {
          id: detail.id,
          type: targetEvent.approvalType,
          typeValue: targetEvent.approvalTypeValue,
          closeReasonId: isTerminate ? values.closeReasonId : undefined,
          reason: values.reason || undefined,
          ...extras,
        };
        createApproval(params).then(() => {
          methods.onSuccess();
        }).finally(() => {
          setIsLock(false);
        });
      });
    },
  });

  useEffect(() => {
    if (!visible) return;
    if (isTerminateTask) {
      methods.getCloseReasonList();
    } else {
      form.setFieldValue('closeReasonId', undefined);
    }
  }, [visible, isTerminateTask]);

  return (
    <Modal
      title='开发异动'
      open={visible}
      width={480}
      okText='申请'
      onOk={methods.onSubmit}
      onCancel={methods.onCancel}
      className={styles.taskChange}>
      <Form form={form} layout='vertical'>
        <V2FormRadio
          label='异动类型'
          name='type'
          options={typeOptions}
          required
        />
        <ApprovalSelectNodes
          id={detail.id}
          type={currentTargetEvent.approvalType}
          typeValue={currentTargetEvent.approvalTypeValue}
        />
        {isTerminateTask ? <V2FormSelect
          label='关闭理由'
          name='closeReasonId'
          options={closeReasonOptions}
          required
        /> : <></>}

        <V2FormTextArea
          label={isTerminateTask ? '具体理由' : '申请理由'}
          name='reason'
          maxLength={500}
          required={!isTerminateTask}
          config={{
            showCount: true
          }}
        />
      </Form>
    </Modal>
  );
};

export default TaskChangeModal;
